import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { UserService } from "@main/users";
import { FormButtonsConfig } from "@mipro/components";
import { IEpisode } from "@project/models/episodes/episode";
import { MessageService, SelectItem } from "primeng/api";
import { FormIODefinition } from "@features/bb-formio";
import { FormIODefinitionService } from "@features/bb-formio/services/formio-definition.service";
import { Dialog } from "primeng/dialog";
import { FormioTagsService } from "@main/formio/services/formio-tags.service";
import { FormTagDTO } from "@main/formio/dto/formTagDTO";
import { Dropdown } from "primeng/dropdown";
import { FormRequestDTO, RequestTargets } from "@main/formio/dto/formRequestDTO";
import { FormioRequestService } from "@main/formio/services/formioRequest.service";
import { MultiUserFormAssociation, NewMultiUserFormAssociation } from "@main/formio/multi-user-form/dto/multi-user-form-associations.dto";
import { MultiUserFormService } from "@main/formio/multi-user-form/services/multi-user-form.service";
import { MultiUserFormDef } from "@main/formio/multi-user-form/dto/multi-user-form.dto";
import { MultiUserFormAssociationsService } from "@main/formio/multi-user-form/services/multi-user-form-associations.service";
import { WalkInService } from "@project/walk-ins/classes/walk-in.service";
import { CustomComponentTypes } from "../data-insertion-dialog/customComponentTypes";
import { WalkIn } from "@project/walk-ins/classes/walk-in";

@Component({
    selector: "formio-filtered-form-select-dialog",
    templateUrl: "./formio-filtered-form-select-dialog.component.html"
})
export class FormIOFilteredFormSelectDialogComponent implements OnInit {
    tagOptions: SelectItem[] = [];
    targetUsers: SelectItem[] = [];
    targetGroups: SelectItem[] = [];
    selectedTargetUsers: SelectItem[] = [];
    selectedTargetGroups: SelectItem[] = [];
    definitionDropdownOptions: SelectItem[] = [];

    allTags: FormTagDTO[] = [];
    selectedDefinitionId: string;
    allDefinitions: FormIODefinition[] = [];

    currentPatientId: string;
    newFormRequest: FormRequestDTO = null;
    displayNewFormRequestDialog: boolean = false;

    currentUrl: string = null;
    formLoaded: boolean = false;
    loadingTags: boolean = false;
    loadingDefinitions: boolean = false;
    displayMufCreateInstanceDialog: boolean = false;

    currentMUFDef: MultiUserFormDef = null;
    currentFormDef: FormIODefinition = null;
    mufAssoc: MultiUserFormAssociation = null;
    newMufAssocData: NewMultiUserFormAssociation = null;

    buttonsConfig: FormButtonsConfig = <FormButtonsConfig>{
        saveLabel: "Select",
        saveFunc: (): void => {
            this.select().then();
        },
        cancelFunc: (): void => {
            this.cancel();
        }
    };

    @Input() moduleId: string = null;
    @Input() visible: boolean = true;
    @Input() moduleName: string = null;
    @Input() categories: string[];
    @Input() currentEpisode: IEpisode = null;
    @Input() isReadOnlyEpisode: boolean = false;
    @Input() walkInId: string = null;
    @Input() isHomePage: boolean = false;
    @Input() noteDraftId: string = null;
    @Input() returnUrl: string = null;

    @Output() readonly onCancel: EventEmitter<void> = new EventEmitter<void>();
    @Output() readonly dialogClosed: EventEmitter<void> = new EventEmitter<void>();
    @Output() readonly onSelect: EventEmitter<FormIODefinition> = new EventEmitter<FormIODefinition>();

    @ViewChild("selectFormDialog") dialog: Dialog
    @ViewChild("formDropdown") formDropdownComponent: Dropdown;

    constructor(
        private formIODefinitionService: FormIODefinitionService,
        private formioRequestService: FormioRequestService,
        private formTagService: FormioTagsService,
        private multiUserFormAssociationService: MultiUserFormAssociationsService,
        private multiUserFormService: MultiUserFormService,
        private patientService: PatientService,
        private messageService: MessageService,
        private walkInService: WalkInService,
        private userService: UserService,
        private router: Router) {
    }

    async ngOnInit(): Promise<void> {
        this.loadingDefinitions = true;
        this.currentPatientId = this.patientService.currentPatientId;
        this.currentUrl = this.router.url;

        this.allDefinitions = await this.formIODefinitionService.getForCategoriesByName(this.categories);
        this.allDefinitions = this.allDefinitions.filter(definition =>
            !!definition?.activeRevision && (!definition.activeRevision.mufCapable || definition.activeRevision.isMUFReady));

        if (!!this.walkInId) {
            let _walkIn: WalkIn = await this.walkInService.get(this.walkInId);
            if (_walkIn?.patientId) {
                this.currentPatientId = _walkIn?.patientId;
                this.patientService.currentPatientId = this.currentPatientId;
            }
        }

        this.formLoaded = false;

        await this.generateDefinitionOptions()

        this.loadingDefinitions = false;

        await this.generateTagOptions()

        this.formioRequestService.getRequestTargets().then(data => {
            this.targetGroups = data.groups.map<SelectItem>(x => {
                return {
                    value: x.id,
                    label: x.name
                }
            });
            this.targetUsers = data.users.map<SelectItem>(x => {
                return {
                    value: x.id,
                    label: x.name
                }
            });
        })

        if (this.allDefinitions.length == 1) {
            this.selectedDefinitionId = this.allDefinitions[0].id;
            this.visible = false;
            this.select().then();
            this.dialogClosed.emit();
        }
        this.formLoaded = true;
    }

    async onTagsChanged(event: any) {
        await this.generateDefinitionOptions(event?.value)
    }

    async generateTagOptions() {
        this.loadingTags = true;
        this.allTags = await this.formTagService.getAll();
        this.loadingTags = false;

        this.tagOptions = this.allTags.map(tag => <SelectItem>{
            value: tag.id_original,
            label: tag.name
        });
    }

    async generateDefinitionOptions(tagIds?: number[]) {

        let filterDefinitions = this.allDefinitions;

        if (!!tagIds) {
            filterDefinitions = filterDefinitions.filter(x =>
                x.formTags?.some(tag => tagIds.includes(tag.formTagId)));
        }

        if (!filterDefinitions.some(x => x.id === this.selectedDefinitionId)) {
            this.selectedDefinitionId = null;
            this.formDropdownComponent?.clear();
        }

        this.definitionDropdownOptions = filterDefinitions.map(x => <SelectItem>{
            value: x.id,
            label: x.name
        })
    }

    cancel(): void {
        this.onCancel.emit();

        if (!this.selectedDefinitionId && !!this.returnUrl) {
            this.router.navigate([this.returnUrl]).then();
        }
    }

    async select(): Promise<void> {
        const selectedDefinition = this.allDefinitions.find(x => x.id === this.selectedDefinitionId);

        this.currentFormDef = selectedDefinition;

        if (this.isHomePage) {
            this.onSelect.emit(selectedDefinition);
            return;
        }

        if (selectedDefinition.activeRevision.mufCapable) {
            this.currentMUFDef = await this.multiUserFormService.getByFormRevisionId(selectedDefinition.activeRevisionId);
            this.displayMufCreateInstanceDialog = true;
            return;
        }

        this.onSelect.emit();
        this.redirectToFormioForm(selectedDefinition).then();
    }

    async Request(): Promise<void> {
        try {
            if (this.selectedTargetUsers.some(x => x) || this.selectedTargetGroups.some(x => x)) {
                this.newFormRequest = {
                    userIds: this.selectedTargetUsers.map(x => x.value),
                    groupsIds: this.selectedTargetGroups.map(x => x.value),
                    formRevisionId: this.currentFormDef.activeRevision.id,
                    requestDate: new Date(),
                    patientId: this.currentPatientId != null ? this.currentPatientId : this.currentEpisode.patientUserId,
                    episodeId: this.currentEpisode.id,
                    isReadOnlyEpisode: this.isReadOnlyEpisode
                }

                await this.formioRequestService.createRequest(this.newFormRequest);
            } else {
                this.messageService.add({
                    severity: "info",
                    summary: "Form request",
                    detail: "You must select at least 1 user or group."
                });
            }
        } catch {
        } finally {
            this.cancelRequest();
        }
    }

    cancelRequest(): void {
        this.newFormRequest = null;
        this.displayNewFormRequestDialog = false;
        this.visible = false;
    }

    cancelPublish(): void {
        this.selectedTargetGroups = [];
        this.selectedTargetUsers = [];
    }

    async redirectToFormioForm(form: FormIODefinition): Promise<void> {
        if (form.byRequestOnly) {
            this.displayNewFormRequestDialog = true;
            return;
        }

        let queryParams = {};

        queryParams["formId"] = form.id;
        queryParams["revisionId"] = form.activeRevision.id;
        queryParams["patientId"] = this.patientService.currentPatientId != null ? this.patientService.currentPatientId : this.currentEpisode?.patientUserId;

        if (this.currentEpisode) queryParams["episodeId"] = this.currentEpisode.id;
        if (this.walkInId) queryParams["walkInId"] = this.walkInId;

        queryParams["generatePdf"] = form.activeRevision.shareOnComplete;
        queryParams["isReadOnlyEpisode"] = this.isReadOnlyEpisode;
        queryParams["noteDraftId"] = this.noteDraftId;

        queryParams["returnUrl"] = this.currentUrl;

        const componentTypes: CustomComponentTypes = await this.formIODefinitionService.getCustomComponentTypes(form.id)
        if (componentTypes &
            (CustomComponentTypes.ConsultationHistoryV2
                | CustomComponentTypes.ConsultationHistory
                | CustomComponentTypes.EpisodeNotes
                | CustomComponentTypes.MedicationHistory)) {
            await this.redirectToDataInsertion(queryParams);
            return;
        }

        const url = this.router.serializeUrl(this.router.createUrlTree(["app/formio/display"], {queryParams}));

        this.router.navigateByUrl(url).then();
    }

    onCancelMufInstanceDialog(): void {
        this.currentMUFDef = null;
        this.mufAssoc = null;
        this.displayMufCreateInstanceDialog = false;
    }

    async onCreateMufInstanceDialog(mufAssocData: NewMultiUserFormAssociation) {
        this.newMufAssocData = mufAssocData;
        this.displayMufCreateInstanceDialog = false;
        this.newMufAssocData.isReadOnlyEpisode = this.isReadOnlyEpisode;
        this.mufAssoc = await this.multiUserFormAssociationService.addNewMUFAssociation(this.newMufAssocData);
        this.redirectToMUFInstance();
    }

    redirectToMUFInstance(): void {
        let startMufSelf = this.mufAssoc.multiUserFormAssociationLinks
            .find(x => x.multiUserFormStage.sequenceStepIndex == 1)?.userId == this.userService.currentUser.id;
        this.currentMUFDef = null;
        this.displayMufCreateInstanceDialog = false;

        if (startMufSelf) {
            const queryParams = {
                mufId: this.mufAssoc.id,
                target: this.userService.currentUser.id,
                formDataId: this.mufAssoc.formDataId,
                isReadOnlyEpisode: this.isReadOnlyEpisode,
                noteDraftId: this.noteDraftId,
                returnUrl: this.currentUrl
            }

            this.router.navigate(["app/formio/multiuser/display"], {queryParams}).then();
        }
        this.mufAssoc = null;
    }

    async redirectToDataInsertion(queryParams: { [key: string]: string | number; }): Promise<void> {
        await this.router.navigate(["app/formio/data-insertion"], {queryParams});
    }
}
